import {
  FloatingPortal,
  Placement,
  useMergeRefs,
  useTransitionStyles,
} from "@floating-ui/react";
import * as Sentry from "@sentry/react";
import DOMPurify from "dompurify";
import {
  HTMLProps,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
} from "react";

import AppError from "@components/error/error";
import { classNames } from "@helpers/css";

import { useTooltip } from "./tooltip-helpers";

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

const useTooltipState = () => {
  const context = useContext(TooltipContext);
  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }
  return context;
};

const TooltipTrigger = forwardRef<HTMLElement, HTMLProps<HTMLElement>>(
  function TooltipTrigger({ children, ...props }, propRef) {
    const state = useTooltipState();

    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([state.refs.setReference, propRef, childrenRef]);

    if (isValidElement(children)) {
      return cloneElement(
        children,
        state.getReferenceProps({
          ref,
          ...props,
          ...children.props,
          "data-state": state.context.open ? "open" : "closed",
        }),
      );
    }

    if (import.meta.env.DEV) {
      throw new Error("TooltipTrigger must be a valid React element");
    } else {
      Sentry.captureException("TooltipTrigger must be a valid React element");
      return children;
    }
  },
);

const looksLikeHTML = (str: string) => {
  return /<([a-z][\s\S]*?)>/i.test(str);
};

const TooltipContent = forwardRef<HTMLDivElement, { children: string }>(
  function TooltipContent({ children, ...props }, propRef) {
    const state = useTooltipState();
    const ref = useMergeRefs([state.refs.setFloating, propRef]);

    const { isMounted, styles } = useTransitionStyles(state.context, {
      duration: 0,
      initial: { opacity: 0 },
    });

    if (!isMounted) return null;
    const cleanHtml =
      typeof children === "string" && looksLikeHTML(children)
        ? DOMPurify.sanitize(children)
        : children;

    return (
      <FloatingPortal>
        <div
          ref={ref}
          className={classNames(
            "bg-black/80 text-white rounded-md text-sm py-1 px-2 max-w-64 z-tooltip",
            looksLikeHTML(children) ? "" : "whitespace-pre-wrap",
          )}
          style={{
            ...state.floatingStyles,
            ...styles,
          }}
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
          {...state.getFloatingProps(props)}
        />
      </FloatingPortal>
    );
  },
);

export const Tooltip = ({
  children,
  text,
  placement = "top",
  delay = 300,
}: {
  children: any;
  text?: null | string;
  placement?: Placement;
  delay?: number;
}) => {
  const tooltip = useTooltip({ placement, delay });

  if (isValidElement(children) && typeof children.type !== "string") {
    if (
      typeof children.type === "function" ||
      typeof children.type === "object"
    ) {
      if (import.meta.env.DEV) {
        throw new Error(
          `Wrap the child of Tooltip in an HTML element: ${JSON.stringify(
            (children as any)._source,
          )}`,
        );
      }
    }
    return children;
  }

  if (!text) {
    return children;
  }

  return (
    <TooltipContext.Provider value={tooltip}>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </TooltipContext.Provider>
  );
};

const TooltipWithSentry = Sentry.withErrorBoundary(Tooltip, {
  fallback: <AppError description={"The tooltip could not be displayed."} />,
});

export default TooltipWithSentry;
